import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
// API
import { GET_TIP_DETAILS } from 'api/tips/queries';
// Types
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
import {
  GetMediaPost_getMediaPost_ImagePost_store,
  GetMediaPost_getMediaPost_VideoPost_store,
} from 'api/mediaPost/types/GetMediaPost';
import {
  GetTipDetails,
  GetTipDetailsVariables,
} from 'api/tips/types/GetTipDetails';
import { TipType } from 'api/graphql-global-types';

type TipSuccessData = {
  amount: number;
  message?: string;
  storeName: string;
  store:
    | GetStoreBySlug_store
    | GetStream_stream_store
    | GetMediaPost_getMediaPost_ImagePost_store
    | GetMediaPost_getMediaPost_VideoPost_store;
  id?: string;
};

export const useTipSuccessModal = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [tipData, setTipData] = useState<TipSuccessData | null>(null);
  const [shouldFetchTip, setShouldFetchTip] = useState(false);

  const { tipSuccess, tipId } = router.query;

  // Fetch tip details when we have a tipId
  const { loading: tipLoading } = useQuery<
    GetTipDetails,
    GetTipDetailsVariables
  >(GET_TIP_DETAILS, {
    variables: {
      input: {
        id: tipId as string,
      },
    },
    skip: !shouldFetchTip || !tipId,
    onCompleted: (data) => {
      if (data?.tipById) {
        const tip = data.tipById;
        let storeName = '';
        let store: any = {};

        // Extract store information based on tip type
        if (tip.type === TipType.User && 'recipient' in tip) {
          const recipient = tip.recipient;
          storeName =
            recipient.storeDetails?.storeName ||
            `${recipient.firstName} ${recipient.lastName}`;
          store = {
            id: recipient.id,
            firstName: recipient.firstName,
            lastName: recipient.lastName,
            slug: recipient.slug,
            storeDetails: recipient.storeDetails,
          };
        } else if (tip.type === TipType.Stream && 'stream' in tip) {
          const streamStore = tip.stream.store;
          storeName =
            streamStore.storeDetails?.storeName ||
            `${streamStore.firstName} ${streamStore.lastName}`;
          store = {
            id: streamStore?.id,
            firstName: streamStore.firstName,
            lastName: streamStore.lastName,
            storeDetails: streamStore.storeDetails,
          };
        }

        const tipSuccessData: TipSuccessData = {
          amount: tip.amount,
          message: tip.message || undefined,
          storeName,
          store,
          id: tip.id,
        };

        setTipData(tipSuccessData);
        setIsOpen(true);
      }
    },
  });

  useEffect(() => {
    if (tipSuccess === 'true' && tipId) {
      setShouldFetchTip(true);
    }
  }, [router.query, tipSuccess, tipId]);

  const closeTipSuccessModal = () => {
    setIsOpen(false);
    setTipData(null);
    setShouldFetchTip(false);

    // Clean up URL parameters when modal is closed
    if (tipSuccess === 'true' && tipId) {
      const cleanQuery = { ...router.query };
      delete cleanQuery.tipSuccess;
      delete cleanQuery.tipId;
      router.replace(
        {
          pathname: router.pathname,
          query: cleanQuery,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return {
    isOpen,
    tipData,
    closeTipSuccessModal,
    loading: tipLoading,
  };
};
